import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Alert from 'components/global/Alert';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import IDVerifyModal from 'modals/IDVerifyModal';
import MobileVerifyModal from 'modals/MobileVerifyModal';

import useCreateShipment from 'pages/Shipments/ActionForms/CreateShipment/Components/useCreateShipment';
import useInternationalOrderCheck from 'pages/Shipments/Shipments/OrderForm/useInternationalOrderCheck';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from './QuickShipment.styled';
import OrderDetails from './QuickShipmentTabs/OrderDetails';
import PickupLocation from './QuickShipmentTabs/PickupLocation';
import ReceiverDetails from './QuickShipmentTabs/ReceiverDetails';

const QuickShipment = ({
	pickupLocationVisible,
	nextStep,
	prevStep,
	tab,
	pickupType,
	setPickupType,
	formMode,
	setFormMode,
	pickupLocationCreated,
	setPickupLocationCreated
}) => {
	const { Country } = useLocale();
	const { showError, showSuccess } = useAlert();
	const axios = useAxiosPrivate();
	const user = useSelector((state) => state.user);
	const navigate = useNavigate();
	const [shipmentCreationLoading, setShipmentCreationLoading] = useState(false);
	const orderPickUpLocationRef = useRef();
	const [pickupSavedFunctionLoading, setPickupSavedFunctionLoading] = useState(false);
	const createShipment = useCreateShipment({ showDefaultAmount: true });
	const isTrUser = useMemo(() => Country === 'TR', [Country]);
	const isEnterpriseUser = user === 'EnterprisePackage';

	const formMethods = useFormContext();
	const {
		handleSubmit,
		setValue,
		getValues,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const parseFullName = (fullName) => {
		const nameParts = fullName.trim().split(/\s+/);
		if (nameParts.length === 1) {
			return { firstName: nameParts[0], lastName: '' };
		}
		return {
			firstName: nameParts.slice(0, -1).join(' '),
			lastName: nameParts[nameParts.length - 1]
		};
	};

	const setCreatedProductIDs = (productIds) => {
		const data = getValues();
		let productIdIndex = 0;
		data.products.map((product) => {
			if (!product.ID) {
				product.ID = productIds[productIdIndex];
				productIdIndex += 1;
			}
		});
		setValue('products', data.products, { shouldValidate: true, shouldDirty: true });
	};

	const onSubmit = async (data) => {
		const newData = { ...data };
		newData.products = newData.products.map((product) => ({
			total: product.total,
			tax: Number(product.tax),
			price: product.price,
			quantity: product.quantity,
			productID: product.productID,
			SKU: product.SKU,
			name: product.name,
			hsCode: product.hsCode,
			ID: product.ID ? product.ID : undefined
		}));
		const { firstName, lastName } = parseFullName(newData.fullName);
		const addOrderData = {
			firstName,
			lastName,
			warehouseID: newData.warehouseID,
			branchID: newData.branchID,
			email: newData.email,
			phone: newData.phone,
			address: newData.address,
			orderID: newData.otoID,
			orderDate: newData.orderDate,
			orderComments: newData.orderComments,
			brandID: newData.brandID,
			[formMode === 'add' ? 'items' : 'products']: newData.products,
			paymentMethod: newData.paymentMethod,
			grandTotal: Number(newData.grandTotal),
			totalDue: newData.paymentMethod === 'cod' ? Number(newData.totalDue) : null,
			weight: Number(newData.weight),
			packageCount: Number(newData.packageCount),
			refNo: newData.refNo,
			currency: newData.currency,
			boxes: newData.boxes,
			otoOrderID: formMode === 'add' ? undefined : watch('orderID')
		};

		await axios
			.post(formMode === 'add' ? ORDER_ACTIONS.ADD_ORDER : ORDER_ACTIONS.UPDATE_ORDER, addOrderData)
			.then(async (response) => {
				const { status, data } = response;
				if (status === 200) {
					setCreatedProductIDs(data.idList);
					setValue(
						'destinationAddress',
						{
							cityName: newData.address.cityName ?? null,
							countryName: newData.address.countryName ?? null,
							zipCode: newData.address.zipCode ?? null,
							lat: newData.address.lat ?? null,
							lng: newData.address.lng ?? null,
							text: newData.address.text ?? null
						},
						{ shouldValidate: true, shouldDirty: true }
					);
					const createShipmentData = {
						items: [
							{
								orderID: formMode === 'add' ? data.ID : newData.orderID,
								manualShipment: true,
								dcSettingsID: watch('dcCompanyCompanyId'),
								boxes: newData.boxes,
								price: newData.price,
								priceLoading: false,
								package: newData.packageCount,
								customCurrency: newData.customCurrency,
								packageWeight: newData.weight
							}
						]
					};
					if (formMode === 'add') {
						setFormMode('edit');
						setValue('ID', newData.orderID);
						setValue('orderID', data.ID);

						if (pickupType === 'otoRates') {
							nextStep();
						} else {
							handleCreateShipment(createShipmentData);
						}
					} else {
						if (pickupType === 'otoRates') {
							setValue('reloadPrices', true);
							nextStep();
						} else {
							handleCreateShipment(createShipmentData);
						}
					}
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.F010') {
					showError({
						message: 'error.orderNumberAlreadyExists'
					});
				} else if (error.response?.data.code) {
					showError({
						message: error.response?.data.code
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const handleCreateShipment = async (createShipmentData) => {
		setShipmentCreationLoading(true);
		await createShipment(createShipmentData, () => {
			showSuccess();
			navigate(PATHS.SHIPMENTS);
		});
		setShipmentCreationLoading(false);
	};

	const quickShipmentTabs = () => {
		switch (tab) {
			case pickupLocationVisible === true ? 0 : -1:
				return (
					pickupLocationVisible === true && (
						<PickupLocation
							nextStep={nextStep}
							ref={orderPickUpLocationRef}
							setPickupLocationCreated={setPickupLocationCreated}
						/>
					)
				);
			case pickupLocationVisible === true ? 1 : 0:
				return <ReceiverDetails formMode={formMode} />;
			case pickupLocationVisible === true ? 2 : 1:
				return <OrderDetails formMode={formMode} pickupType={pickupType} setPickupType={setPickupType} />;
		}
	};

	const { isInternationalOrder } = useInternationalOrderCheck({
		orderCountryCode: watch('address.countryName'),
		pickupLocationCountryCode: watch('originCountry')
	});

	useEffect(() => {
		setValue('isInternationalOrder', isInternationalOrder);
	}, [isInternationalOrder, setValue]);

	return (
		<S.CreateShipmentContainer>
			{(!user.mobileVerified || !user.nationalIDVerified) && (
				<S.VerificationWrapper>
					{!user.mobileVerified ? (
						<Alert
							showIcon
							ctaButtonLabel="accountRemoval.verifyMobileNumberHeader"
							onCtaButtonClick={() => NiceModal.show(MobileVerifyModal)}
							variant="error"
							header="verificationNeeded"
							content="verifyMobileDescription"
						/>
					) : null}
					{!user.nationalIDVerified && isTrUser && !isEnterpriseUser && (
						<Alert
							showIcon
							ctaButtonLabel="verifyId"
							onCtaButtonClick={() => NiceModal.show(IDVerifyModal)}
							variant="error"
							header="verificationNeeded"
							content="verifyIDText"
						/>
					)}
				</S.VerificationWrapper>
			)}

			{quickShipmentTabs()}

			<S.CreateShipmentBottom>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={() => {
						navigate(-1);
					}}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>

				{tab <= (pickupLocationVisible === true ? 2 : 1) && tab > 0 && !(tab === 1 && pickupLocationCreated) ? (
					<Button label="previous" variant="outline" icon={faAngleLeft} onClick={prevStep} />
				) : null}
				{(tab === 0 && pickupLocationVisible === false) || (tab === 1 && pickupLocationVisible === true) ? (
					<Button
						icon={faAngleRight}
						label="quickShipment.continue"
						iconPos="right"
						onClick={handleSubmit(nextStep, (errors) => {
							AppLogger.log(errors);
						})}
						variant="blue"
						loading={isLoading}
					/>
				) : null}

				{tab === 0 && pickupLocationVisible === true && (
					<Button
						icon={faAngleRight}
						label="quickShipment.continue"
						iconPos="right"
						onClick={async () => {
							setPickupSavedFunctionLoading(true);
							await orderPickUpLocationRef.current?.save();
							setPickupSavedFunctionLoading(false);
						}}
						variant="blue"
						loading={pickupSavedFunctionLoading}
					/>
				)}
				{tab === (pickupLocationVisible === true ? 2 : 1) ? (
					<Button
						icon={faAngleRight}
						disabledTooltip={
							pickupType === 'myRates'
								? 'quickShipment.selectDcCompany'
								: 'quickShipment.verificationTooltip'
						}
						disabled={
							(!user.nationalIDVerified && isTrUser) ||
							!user.mobileVerified ||
							(pickupType === 'myRates' && !watch('dcCompanyCompanyId'))
						}
						label={pickupType === 'otoRates' ? 'quickShipment.continue' : 'createShipment'}
						iconPos="right"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						variant="blue"
						loading={isLoading || shipmentCreationLoading}
					/>
				) : null}
			</S.CreateShipmentBottom>
		</S.CreateShipmentContainer>
	);
};

export default QuickShipment;
